import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable paginator: prev + windowed page buttons (5 visible) + ellipses + next.
 * Extracted/promoted from MarketplacePagination (Session 30b) into a generic
 * component during the Session 31 pagination rollout.
 *
 * Pass a custom `testidPrefix` (default "pagination") so multiple paginators
 * on the same page have distinct, scrapeable testids.
 *
 * Props:
 *   page         (number)  1-indexed current page
 *   totalPages   (number)  total pages
 *   onChange     (fn)      called with the new page number
 *   testidPrefix (string)  prefix for data-testid (default "pagination")
 *   className    (string)  optional extra class on the wrapper
 */
export function Pagination({ page, totalPages, onChange, testidPrefix = "pagination", className = "", theme = "dark" }) {
  if (!totalPages || totalPages <= 1) return null;

  const pages = [];
  let start = Math.max(1, page - 2);
  let end = Math.min(totalPages, start + 4);
  if (end - start < 4) start = Math.max(1, end - 4);

  const palette = theme === "light"
    ? {
        baseColor: "#1a1a18",
        activeBg:  "rgba(27,58,45,0.08)",
        activeBorder: "rgba(27,58,45,0.25)",
        activeColor: "#1b3a2d",
        ellipsis: "rgba(0,0,0,0.3)",
        stepBorder: "rgba(0,0,0,0.12)",
        stepBg: "rgba(0,0,0,0.03)",
        stepDisabledColor: "rgba(0,0,0,0.25)",
      }
    : {
        baseColor: "#f5f0e8",
        activeBg:  "rgba(201,168,76,0.12)",
        activeBorder: "rgba(201,168,76,0.3)",
        activeColor: "#c9a84c",
        ellipsis: "rgba(245,240,232,0.2)",
        stepBorder: "rgba(255,255,255,0.08)",
        stepBg: "rgba(255,255,255,0.03)",
        stepDisabledColor: "rgba(245,240,232,0.2)",
      };

  const baseBtn = {
    padding: "8px 12px", borderRadius: 8, fontSize: 13,
    border: "1px solid transparent", background: "transparent",
    color: palette.baseColor, cursor: "pointer", fontWeight: 400,
    transition: "background 0.15s",
  };
  const activeBtn = {
    ...baseBtn,
    border: `1px solid ${palette.activeBorder}`,
    background: palette.activeBg,
    color: palette.activeColor,
    fontWeight: 700,
  };
  const ellipsisStyle = { color: palette.ellipsis, fontSize: 13 };

  if (start > 1) {
    pages.push(
      <button key={1} onClick={() => onChange(1)} style={baseBtn}>1</button>
    );
    if (start > 2) pages.push(<span key="ds" style={ellipsisStyle}>...</span>);
  }

  for (let i = start; i <= end; i++) {
    pages.push(
      <button
        key={i}
        onClick={() => onChange(i)}
        data-testid={`${testidPrefix}-page-${i}`}
        style={i === page ? activeBtn : baseBtn}
      >
        {i}
      </button>
    );
  }

  if (end < totalPages) {
    if (end < totalPages - 1) pages.push(<span key="de" style={ellipsisStyle}>...</span>);
    pages.push(
      <button key={totalPages} onClick={() => onChange(totalPages)} style={baseBtn}>{totalPages}</button>
    );
  }

  const stepBtn = (disabled) => ({
    padding: "8px 14px", borderRadius: 8,
    border: `1px solid ${palette.stepBorder}`,
    background: palette.stepBg,
    color: disabled ? palette.stepDisabledColor : palette.baseColor,
    cursor: disabled ? "default" : "pointer", fontSize: 13,
  });

  return (
    <div
      className={className}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 24, paddingBottom: 8 }}
      data-testid={testidPrefix}
    >
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        data-testid={`${testidPrefix}-prev`}
        style={stepBtn(page === 1)}
      >
        <ChevronLeft size={14} />
      </button>
      {pages}
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        data-testid={`${testidPrefix}-next`}
        style={stepBtn(page === totalPages)}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

export default Pagination;
